import { useCallback, useEffect, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { fmtTime, useCtx } from '../lib.ts';

interface Account {
  userId: string;
  displayName: string;
  role: string;
  operatorId: string | null;
  createdAt: string;
  createdBy: string | null;
  lastLoginAt: string | null;
  mfaEnrolled: boolean;
  passwordSet: boolean;
  passwordChangedAt: string | null;
  lockedUntil: string | null;
  deletedAt: string | null;
}
interface RoleInfo {
  role: string;
  label: string;
  summary: string;
}

/** Admins only: portal accounts and roles. Every change is audited; secrets are shown once. */
export function Users() {
  const { backend, user, data } = useCtx();
  const server = backend.server;
  const [list, setList] = useState<{ users: Account[]; roles: RoleInfo[]; operators?: { operatorId: string; name: string }[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<{ userId: string; password: string; kind: 'created' | 'reset' } | null>(null);
  const [form, setForm] = useState({ userId: '', displayName: '', role: 'OPERATIONS', operatorId: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const load = useCallback(async () => {
    if (!server) return;
    const r = await server.get<{ users: Account[]; roles: RoleInfo[]; operators?: { operatorId: string; name: string }[] }>('/v1/admin/users');
    if (r.ok) setList(r.value);
    else setError(r.error);
  }, [server]);
  useEffect(() => {
    const t = setTimeout(() => void load(), 0);
    return () => clearTimeout(t);
  }, [load]);
  if (!server) return <p className="empty">User management needs the UrbanRats backend.</p>;
  if (!list) return <p className="empty">{error ?? 'Loading accounts…'}</p>;
  const label = (r: string) => list.roles.find((x) => x.role === r)?.label ?? r;
  const operators = list.operators?.map((o) => o.operatorId) ?? [...new Set(data.tenants.map((t) => t.operatorId))];
  const ask = (what: string) => {
    const r = prompt(`${what}\n\nReason (at least 10 characters, goes into the audit log):`);
    return r && r.trim().length >= 10 ? r.trim() : null;
  };
  const act = async (f: () => Promise<{ ok: true; value: unknown } | { ok: false; error: string }>, onOk?: (v: unknown) => void) => {
    setBusy(true);
    setError(null);
    const r = await f();
    setBusy(false);
    if (!r.ok) return setError(r.error);
    onOk?.(r.value);
    await load();
  };
  const shown = list.users.filter((u) => showDeleted || !u.deletedAt);
  const active = list.users.filter((u) => !u.deletedAt);

  return (
    <div className="users-page" data-testid="users-page">
      <div className="cc-head">
        <div>
          <h1>Users &amp; roles</h1>
          <p className="note">Sign-in: password + authenticator app. New accounts get a one-time password and enrol the app (QR code) at their first sign-in. Only admins manage accounts; every change is audited.</p>
        </div>
        <div className="cc-head-tags">
          <span className="pill">{active.length} active accounts</span>
          <span className="pill">{active.filter((u) => !u.mfaEnrolled).length} waiting for first sign-in</span>
        </div>
      </div>

      {secret && (
        <section className="card-x secret-box" role="status" data-testid="user-secret">
          <header className="card-h">
            <h2>
              <Icon name="alert" size={16} /> One-time password for {secret.userId}
            </h2>
          </header>
          <p>
            Give it to the person over a separate channel. It is shown <strong>only now</strong>; at the first sign-in they scan the authenticator QR code and are asked to choose their own password.
          </p>
          <div className="secret-row">
            <code data-testid="user-secret-value">{secret.password}</code>
            <button type="button" className="btn" onClick={() => void navigator.clipboard?.writeText(secret.password)}>
              Copy
            </button>
            <button type="button" className="btn ghost" onClick={() => setSecret(null)}>
              Done
            </button>
          </div>
        </section>
      )}
      {error && (
        <p className="warn-box" role="alert">
          {error}
        </p>
      )}

      <div className="users-grid">
        <section className="card-x" data-testid="users-table">
          <header className="card-h">
            <h2>Accounts</h2>
            <label className="note toggle">
              <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} /> show deleted
            </label>
          </header>
          <ul className="user-list">
            {shown.map((u) => (
              <li key={u.userId} className={u.deletedAt ? 'deleted' : ''} data-testid={`user-${u.userId}`}>
                <span className="avatar" aria-hidden>
                  {u.displayName.slice(0, 2).toUpperCase()}
                </span>
                <span className="who">
                  <strong>
                    {u.displayName} <span className="mono muted">{u.userId}</span>
                  </strong>
                  <span className="muted">
                    {u.deletedAt ? `deleted ${fmtTime(u.deletedAt)}` : u.lastLoginAt ? `last sign-in ${fmtTime(u.lastLoginAt)}` : 'never signed in'}
                    {u.operatorId ? ` · operator ${u.operatorId}` : ''}
                  </span>
                </span>
                {u.deletedAt || u.userId === user.userId ? (
                  <span className={`role-tag role-${u.role}`} title={u.userId === user.userId ? 'You cannot change your own role' : undefined}>
                    {label(u.role)}
                  </span>
                ) : (
                  <select
                    className={`role-tag role-select role-${u.role}`}
                    value={u.role}
                    disabled={busy}
                    aria-label={`Role of ${u.userId}`}
                    data-testid={`role-${u.userId}`}
                    onChange={(e) => {
                      const role = e.target.value;
                      const operatorId = role === 'PARTNER_ADMIN' ? prompt(`Operator of this partner admin (one of: ${operators.join(', ')})`, operators[0] ?? '')?.trim() : undefined;
                      if (role === 'PARTNER_ADMIN' && !operatorId) return;
                      const reason = ask(`Change the role of ${u.userId}: ${label(u.role)} → ${label(role)}. Their open sessions end; the new permissions apply at the next sign-in.`);
                      if (reason) void act(() => server.post(`/v1/admin/users/${encodeURIComponent(u.userId)}/role`, { role, ...(operatorId ? { operatorId } : {}), reason }));
                    }}
                  >
                    {list.roles.map((r) => (
                      <option key={r.role} value={r.role}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                )}
                <span className="sec">
                  <span className={`tag ${u.mfaEnrolled ? 'st-OK' : 'st-PENDING'}`}>{u.mfaEnrolled ? '2FA on' : '2FA at first sign-in'}</span>
                  {!u.passwordSet && <span className="tag st-UNKNOWN" title="DEV seeded account: code-only sign-in, refused in production">DEV · no password</span>}
                  {u.passwordSet && !u.passwordChangedAt && <span className="tag st-PENDING">temporary password</span>}
                  {u.lockedUntil && <span className="tag st-FAILED">locked</span>}
                </span>
                {!u.deletedAt && (
                  <span className="actions">
                    <button
                      type="button"
                      className="btn ghost"
                      disabled={busy}
                      onClick={() => {
                        const reason = ask(`Reset ${u.userId}: new one-time password, authenticator enrolled again, all sessions end.`);
                        if (reason) void act(() => server.post(`/v1/admin/users/${encodeURIComponent(u.userId)}/reset`, { reason }), (v) => setSecret({ userId: u.userId, password: (v as { temporaryPassword: string }).temporaryPassword, kind: 'reset' }));
                      }}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="btn danger"
                      disabled={busy || u.userId === user.userId}
                      title={u.userId === user.userId ? 'You cannot delete your own account' : undefined}
                      data-testid={`delete-${u.userId}`}
                      onClick={() => {
                        const reason = ask(`Delete ${u.userId}? The account is disabled at once (kept for the audit trail).`);
                        if (reason) void act(() => server.del(`/v1/admin/users/${encodeURIComponent(u.userId)}`, { reason }));
                      }}
                    >
                      Delete
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>

        <div className="cc-side">
          <section className="card-x" data-testid="user-create">
            <header className="card-h">
              <h2>Add a user</h2>
            </header>
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                void act(
                  () => server.post('/v1/admin/users', { userId: form.userId.trim(), displayName: form.displayName.trim(), role: form.role, operatorId: form.role === 'PARTNER_ADMIN' ? form.operatorId : undefined, reason: form.reason }),
                  (v) => {
                    setSecret({ userId: form.userId.trim(), password: (v as { temporaryPassword: string }).temporaryPassword, kind: 'created' });
                    setForm({ userId: '', displayName: '', role: form.role, operatorId: '', reason: '' });
                  },
                );
              }}
            >
              <label htmlFor="nu-id">User id</label>
              <input id="nu-id" value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value.toLowerCase() })} placeholder="e.g. ayse.k" autoComplete="off" data-testid="nu-id" />
              <label htmlFor="nu-name">Name</label>
              <input id="nu-name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} autoComplete="off" data-testid="nu-name" />
              <label htmlFor="nu-role">Role</label>
              <select id="nu-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="nu-role">
                {list.roles.map((r) => (
                  <option key={r.role} value={r.role}>
                    {r.label}
                  </option>
                ))}
              </select>
              <p className="note">{list.roles.find((r) => r.role === form.role)?.summary}</p>
              {form.role === 'PARTNER_ADMIN' && (
                <>
                  <label htmlFor="nu-op">Operator</label>
                  <select id="nu-op" value={form.operatorId} onChange={(e) => setForm({ ...form, operatorId: e.target.value })}>
                    <option value="">choose…</option>
                    {operators.map((o) => (
                      <option key={o} value={o}>
                        {o}
                        {list.operators?.find((x) => x.operatorId === o)?.name ? ` — ${list.operators.find((x) => x.operatorId === o)!.name}` : ''}
                      </option>
                    ))}
                  </select>
                </>
              )}
              <label htmlFor="nu-reason">Reason (audit)</label>
              <input id="nu-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. new risk analyst, ticket OPS-12" data-testid="nu-reason" />
              <button type="submit" className="btn primary" disabled={busy || form.userId.trim().length < 2 || form.displayName.trim().length < 2 || form.reason.trim().length < 10} data-testid="nu-submit">
                Create account
              </button>
            </form>
          </section>
          <section className="card-x">
            <header className="card-h">
              <h2>Roles</h2>
            </header>
            <ul className="role-list">
              {list.roles.map((r) => (
                <li key={r.role}>
                  <span className={`role-tag role-${r.role}`}>{r.label}</span>
                  <span className="muted">{r.summary}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
